import { MatchData } from '@bz/socket/src/proto/notification';
import { RouteSectionProps } from '@solidjs/router';
import { useFavList } from '@bz/common';
import { useSocketBusiness } from '@bz/socket';

// import { useMenuContext } from '~/layout/menu/context';
import { MatchLiveSwitcher, MatchMarkets, MatchSidebarIframe, MatchTopNavigation } from '~/components/Match';
import { SUPPORT_LIVE_SPORTS } from '~/constants';
import { getIdFromUrn, getSportTypeById } from '~/utils';
import useSubscribe from '~/primitives/useSubscribe';

const variantLivingOptions = {
  disableComponents: true,
  scoreboard: 'extended',
  collapseTo: 'momentum',
} as const;

export const route = {
  preload: () => {},
} satisfies RouteDefinition;

function BaseMatchPage(props: RouteSectionProps) {
  // viewport >= 1024 ? large layout
  const [isLarge, setIsLarge] = createSignal(false);
  const navigate = useNavigate();
  const { useOddsHandover } = useSocketBusiness();

  const safeMatchId = () => props.params.matchId || '';

  const getMatchDetails = useAction(post.createSportDataMatchGetTournamentMatchByIds);
  const data = createAsync(() => {
    return getMatchDetails({ matchIds: [safeMatchId()] }).then((res) => {
      const item = res?.[0];
      if (item && item.matches?.[0]) {
        return { ...item, ...item.matches[0] } as any;
      }
      return item as any;
    });
  });

  const sportId = createMemo(() => data()?.sportId || '');
  const sportType = createMemo(() => getSportTypeById(sportId()));
  const canShowLiveMatchSidebar = createMemo(() => Boolean(sportType() && SUPPORT_LIVE_SPORTS.includes(sportType()!)));
  const liveMatchEmbedToken = createMemo(() => (account.isLogin ? 'internal' : undefined));

  const { ids } = useFavList();
  const isFavorite = createMemo(() => !!ids()?.find((id) => String(id) === String(safeMatchId())));
  const handleFavoriteClick = () => {};

  onMount(() => {
    if (import.meta.env.SSR) return;
    const update = () => setIsLarge(window.innerWidth >= 950);
    update();
    window.addEventListener('resize', update);
    onCleanup(() => window.removeEventListener('resize', update));
  });
  const matchId = createMemo(() => getIdFromUrn(data()?.id ?? ''));
  const matchHeaderInfo = createMemo(() => ({
    sportName: data()?.sportName,
    categoryName: data()?.categoryName,
    tournamentName: data()?.tournamentName,
    homeName: data()?.homeName,
    awayName: data()?.awayName,
  }));
  const handleBack = () => navigate(-1);

  useSubscribe(() => {
    const id = safeMatchId();
    return id ? [id] : [];
  });

  const marketData = useOddsHandover(safeMatchId());

  createEffect(
    on(
      marketData,
      (handoverCount) => {
        if (!handoverCount) {
          return;
        }
        if (data()?.status === 0) {
          window.location.reload();
        }
      },
      { defer: true }
    )
  );
  return (
    <div class="mb-5 flex flex-col w-full">
      <MatchTopNavigation
        match={matchHeaderInfo()}
        onBack={handleBack}
        onBreadcrumbClick={handleBack}
        onFavoriteClick={handleFavoriteClick}
        isFavorite={isFavorite()}
        matchId={safeMatchId()}
        isShowFavorite={true}
        sportId={data()?.sportId}
        categoryId={data()?.categoryId}
        tournamentId={data()?.tournamentId}
        status={data()?.status}
      />
      <div class="flex">
        <div class="flex-1 min-w-0">
          <MatchLiveSwitcher
            matchId={matchId()}
            match={data() as unknown as MatchData}
            isLargeScreen={isLarge()}
            compactLivingOptions={variantLivingOptions}
          />
          <Show when={data()} keyed>
            {(match) => (
              <AnimateFly>
                <CustomErrorBoundary>
                  <MatchMarkets matchId={safeMatchId()} match={match as any} />
                </CustomErrorBoundary>
              </AnimateFly>
            )}
          </Show>
        </div>
        <Show when={isLarge()}>
          <Show when={!data() || canShowLiveMatchSidebar()}>
            <MatchSidebarIframe
              matchId={canShowLiveMatchSidebar() ? safeMatchId() : undefined}
              embedToken={liveMatchEmbedToken()}
              loading={!data()}
            />
          </Show>
        </Show>
      </div>
    </div>
  );
}

export default BaseMatchPage;
